import { useState, useEffect } from 'react';
import getNextConfig from 'next/config';
import type { Session } from '@hub/hooks/states/useSessionStore';
import {
  FfhFeatureNames,
  type FileMetadata,
  type ProductOrigin,
  type ProductsEventData,
  type StreamProduct,
} from '@igus/iglidur-free-form-hub-interfaces';
import { featureFlagHeader } from '@igus/kopla-domain';
import { EventSourcePolyfill, type MessageEvent as EventSourceMessageEvent } from 'event-source-polyfill';
import { FFH_SERVICE_INT } from '../../config';
import type { Status } from '@hub/free-form-hub/common/uploadFile/useFfhUpload';
import type { NextRuntimeConfiguration } from '@hub/libs/config';
import { EnvironmentName } from '@hub/libs/constants';

const { publicRuntimeConfig } = getNextConfig() as NextRuntimeConfiguration;

const getSlicerPricesFeatureFlag = () => {
  const { envName: environmentName } = publicRuntimeConfig;
  const isEnabled = environmentName === EnvironmentName.BLACK || environmentName === EnvironmentName.WHITE;

  return `${FfhFeatureNames.slicerPrices}=${Number(isEnabled)}`;
};

const getProductKey = (product: StreamProduct) =>
  `${product.material.id}-${product.material.procedure}-${product.origin}`;

const updateProducts = (currentProducts: StreamProduct[], newProducts: StreamProduct[]) => {
  const productsByKey = new Map(currentProducts.map(product => [getProductKey(product), product]));

  newProducts.forEach(product => productsByKey.set(getProductKey(product), product));

  return Array.from(productsByKey.values());
};

export const useStreamProductsData = (
  session: Session,
  fileId: string,
  quantity: number,
  uploadStatus: Status,
  clientReady: boolean
) => {
  const [products, setProducts] = useState<StreamProduct[] | null>(null);
  const [metaData, setMetaData] = useState<FileMetadata | null>(null);
  const [productsOrigins, setProductOrigins] = useState<ProductOrigin[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<boolean>(false);

  useEffect(() => {
    let eventSource: EventSourcePolyfill | null = null;

    const startEventStream = () => {
      if (!session || !fileId || uploadStatus !== 'completed' || !clientReady) {
        return;
      }

      setIsLoading(true);
      setError(null);
      setProducts([]);
      setMetaData(null);
      setProductOrigins([]);
      setDone(false);

      const url = `${FFH_SERVICE_INT}/product/${fileId}/all/stream?sid=${session.sessionId}&server=${session.server}&quantity=${quantity}`;
      eventSource = new EventSourcePolyfill(url, {
        headers: {
          [featureFlagHeader]: getSlicerPricesFeatureFlag(),
        },
      });

      eventSource.addEventListener('metadata', event => {
        try {
          const data = JSON.parse((event as EventSourceMessageEvent).data as string) as FileMetadata;

          setMetaData(data);
        } catch (e) {
          setError(`Failed to parse metadata: ${e instanceof Error ? e.message : String(e)}`);
        }
      });

      eventSource.addEventListener('products', event => {
        try {
          const newProducts = JSON.parse((event as EventSourceMessageEvent).data as string) as ProductsEventData;
          const newOrigins = newProducts.map(product => product.origin);

          setProductOrigins(prev => Array.from(new Set([...prev, ...newOrigins])));
          setProducts(prev => updateProducts(prev ?? [], newProducts));
        } catch (e) {
          setError(`Failed to parse products: ${e instanceof Error ? e.message : String(e)}`);
        }
      });

      eventSource.addEventListener('done', () => {
        setDone(true);
        setIsLoading(false);
        eventSource?.close();
      });

      eventSource.onerror = () => {
        setError('EventSource connection error');
        setIsLoading(false);
        eventSource?.close();
      };
    };

    startEventStream();

    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [clientReady, fileId, quantity, uploadStatus]);

  return {
    products,
    productsOrigins,
    metaData,
    dataIsLoading: isLoading,
    error,
    done,
  };
};
